/**
 * Color theme system — reads/writes `wwi_theme` in localStorage and mirrors
 * it as a `data-theme` attribute on <html> so CSS custom-property overrides
 * in index.css can kick in. Options: "original" | "dark" | "desert".
 */
import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "wwi_theme";
const VALID = new Set(["original", "dark", "desert", "ocean"]);

export const THEMES = [
  {
    key: "original",
    label: "Original",
    description: "Warm cream & orange (default)",
    swatches: ["#FFFDFB", "#F58227", "#E9EDEB"],
  },
  {
    key: "dark",
    label: "Dark",
    description: "Charcoal night — kinder to your eyes late",
    swatches: ["#191816", "#F58227", "#26241F"],
  },
  {
    key: "desert",
    label: "Desert",
    description: "Sand, terracotta, and low sun",
    swatches: ["#FBF0DA", "#F58227", "#EBDBBE"],
  },
  {
    key: "ocean",
    label: "Ocean",
    description: "Seafoam blues with soft coral warmth",
    swatches: ["#CDE9EE", "#FCD9C4", "#DCEBEE"],
  },
];

export const applyTheme = (theme) => {
  const chosen = VALID.has(theme) ? theme : "original";
  const root = document.documentElement;
  if (chosen === "original") {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", chosen);
  }
};

export const getStoredTheme = () => {
  if (typeof window === "undefined") return "original";
  const stored = localStorage.getItem(STORAGE_KEY);
  return VALID.has(stored) ? stored : "original";
};

export const useTheme = () => {
  const [theme, setThemeState] = useState(getStoredTheme);

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(VALID.has(next) ? next : "original");
  }, []);

  return { theme, setTheme };
};
